import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  TablePagination,
  CircularProgress,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { httpService } from '../../services/httpService';
import { API_BASE_URL, REQ_ENC_KEY } from '../../services/api';
import { useSelector } from 'react-redux';
import PageHeader from '../../components/PageHeader';
import { decryptResponse, encryptResponse, getSessionKey } from '../../services';

interface InactiveUserRequest {
  _id: string;
  organization_id: string;
  application_id: string;
  application_name: string;
  organization_name: string;
  participants_ids: string[];
  participant_status: string;
  request_status: string;
  created_at: string;
  updated_at: string;
  __v: number;
}

export default function InactiveUserRequests(): React.ReactElement {
  const userDetails = useSelector((state: any) => state.auth.userDetails);
  const [requests, setRequests] = useState<InactiveUserRequest[]>([]);
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isLoadingRequests, setIsLoadingRequests] = useState<boolean>(true);

  useEffect(() => {
    if (!userDetails) return;
    fetchInactiveUserRequests();
  }, [userDetails]);

  const fetchInactiveUserRequests = async (): Promise<void> => {
    if (!userDetails?.organization_id) return;

    setIsLoadingRequests(true);
    try {
      const payload = {
        action: "get_inactive_user_requests",
        data: await encryptResponse({
          organization_id: userDetails.organization_id
        }, REQ_ENC_KEY)
      };

      const response = await httpService.post<any>(
        `${API_BASE_URL}/user/getInactiveUserRequests`,
        payload
      );

      if (response.data.status === 200 && response.data.data) {
        const data = response.data.data;
        const decrypted = await decryptResponse<{
          all_requests: InactiveUserRequest[];
        }>(data, getSessionKey() as string);
        const { all_requests } = decrypted;
        setRequests(all_requests || []);
      }
    } catch (error) {
      console.log('Error fetching inactive user requests:', error);
      toast.error('Failed to fetch inactive user requests', {
        position: 'top-right',
        autoClose: 3000
      });
    } finally {
      setIsLoadingRequests(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number): void => setPage(newPage);
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const filteredRequests: InactiveUserRequest[] = requests.filter(request =>
    request.application_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.organization_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    request.request_status.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const paginatedRequests: InactiveUserRequest[] = filteredRequests.slice(
    page * rowsPerPage, 
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box sx={{ mb: 4 }}>
        <PageHeader
          heading="Inactive User Requests"
          subheading="View and manage inactive user requests for your applications"
          variant="gradient"
        />
      </Box>

      <Box display="flex" alignItems="center" gap={1} mb={2}>
        <TextField
          placeholder="Search by application, organization, status..."
          size="small"
          value={searchTerm}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchTerm(e.target.value)}
          InputProps={{ endAdornment: <IconButton><SearchIcon /></IconButton> }}
        />
      </Box>

      <Paper>
        {isLoadingRequests ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
            <Typography sx={{ ml: 2 }}>Loading requests...</Typography>
          </Box>
        ) : (
          <>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Application Name</TableCell>
                  <TableCell>Organization Name</TableCell>
                  <TableCell>Participants Count</TableCell>
                  <TableCell>Participant Status</TableCell>
                  <TableCell>Request Status</TableCell>
                  <TableCell>Created At</TableCell>
                  <TableCell>Updated At</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedRequests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: 'center', py: 4 }}>
                      <Typography variant="body2" color="text.secondary">
                        No inactive user requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRequests.map((request) => (
                    <TableRow key={request._id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {request.application_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {request.organization_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={request.participants_ids.length}
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor: request.participant_status === 'InActive' ? '#fff3cd' : '#f5f5f5',
                            color: request.participant_status === 'InActive' ? '#856404' : '#666666'
                          }}
                        >
                          {request.participant_status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            bgcolor:
                              request.request_status === 'Pending' ? '#e3f2fd' :
                              request.request_status === 'Approved' ? '#e8f5e8' :
                              request.request_status === 'Rejected' ? '#ffebee' :
                              '#f5f5f5',
                            color:
                              request.request_status === 'Pending' ? '#1565c0' :
                              request.request_status === 'Approved' ? '#2e7d32' :
                              request.request_status === 'Rejected' ? '#c62828' :
                              '#666666'
                          }}
                        >
                          {request.request_status}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(request.created_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(request.updated_at).toLocaleDateString()}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={filteredRequests.length}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>
    </Box>
  );
}
